import {TAX_TAG_CONTRIBUTION, 
    TAX_TAG_POSTPONED,
    TAX_TAG_ROLLOVER,
    TAX_TAG_DISTRIBUTION,
    TAX_TAG_CONVERSION,
    TAX_TAG_CONVERSION_OUT,
    TAX_TAG_RECHARACTERIZATION,
    TAX_TAG_LATE_ROLLOVER,
    TAX_TAG_TRANSFER_IN,
    TAX_TAG_TRANSFER_OUT
} from "@/views/Account Management/IRA Inflow Outflow/ira-inflow-outflow-const";

export const INCLUDE_ALL_TRANSACTIONS_TAB     = 1;        // 0x00000001
export const INCLUDE_OPEN_ORDERS_TAB          = 2;        // 0x00000002
export const INCLUDE_ALL_ORDERS_TAB           = 4;        // 0x00000004
export const INCLUDE_GAIN_LOSS_TAB            = 8;        // 0x00000008
export const INCLUDE_UNREALIZED_GAIN_LOSS_TAB = 16;       // 0x00000010
export const INCLUDE_CLIENT_BENEFICIARIES_TAB = 32;       // 0x00000020

export const SINGLE_ACCOUNT_VISIBLE_TABS    = 15;       // 0x00001111
export const SINGLE_CLIENT_VISIBLE_TABS     = 7;        // 0x00000111

export const TAB_CONFIG = new Map([
    [ INCLUDE_ALL_TRANSACTIONS_TAB     , { "title": "All Transactions"        , "id": "transactions"             } ]
,   [ INCLUDE_OPEN_ORDERS_TAB          , { "title": "Open Orders"             , "id": "openOrders"               } ]
,   [ INCLUDE_ALL_ORDERS_TAB           , { "title": "All Orders"              , "id": "allOrders"                } ]
,   [ INCLUDE_GAIN_LOSS_TAB            , { "title": "Realized Gain/Loss"      , "id": "gainLossReport"           } ]
,   [ INCLUDE_UNREALIZED_GAIN_LOSS_TAB , { "title": "Unrealized Gain/Loss"    , "id": "unrealizedGainLossReport" } ]
]);

export function getTabConfig(tabVisibility, tabConfigMap = TAB_CONFIG) {
    let tabIndex = 0;
    let tabConfig = [];
    for (const [key, value] of tabConfigMap.entries()) {
        if ((tabVisibility & key) === key) {
            let value_copy = structuredClone(value);
            value_copy["index"] = tabIndex++;
            tabConfig.push(value_copy);
        }
    }
    return tabConfig;
}

export function addBeneficiaryTabIfPermitted(advisorBeneficiaryReadPermission, advisorBeneficiaryWritePermission) {
    if (!advisorBeneficiaryReadPermission && !advisorBeneficiaryWritePermission) {
        return TAB_CONFIG;
    } else {
        // Return a new Map with the extra tab, do not reassign TAB_CONFIG
        return new Map([
            ...TAB_CONFIG.entries(),
            [INCLUDE_CLIENT_BENEFICIARIES_TAB, { "title": "Beneficiary Information", "id": "beneficiaries" }]
        ]);
    }
}

export function getLoadButtonLabel()
{
    return "Additional Details";
}

/*
    In order to auto-scroll to bring the Transactions card into view, the container must have a non-percentage height
    defined.
    Using "vh" doesn't work very well, due to the Gain/Loss tab needing a height in pixels to allow floating header
    and scrolling contents.

    Thus, I need to dynamically calculate the height in pixels.
*/
function calculateTransactionsCardHeight()
{    
    return parseInt(window.innerHeight * 0.65);
}

function calculateGainLossDetailsContainerHeight()
{
    const TAB_HEIGHT = 49;
    const CALC_METHOD_HEIGHT = 66;
    const SUB_TAB_HEIGHT = 48;
    const DETAIL_HEADER_HEIGHT = 150;
    const REPORT_HEADER_HEIGHT = 78;
    const FIXED_HEIGHTS = 
            TAB_HEIGHT + CALC_METHOD_HEIGHT + 
            SUB_TAB_HEIGHT + DETAIL_HEADER_HEIGHT +
            REPORT_HEADER_HEIGHT;

    return Math.max(calculateTransactionsCardHeight()- FIXED_HEIGHTS, 0);
}

export function getTransactionsCardHeightCss()
{
    return "height: " + calculateTransactionsCardHeight() + "px";
}

export function getGainLossDetailsContainerHeightCss()
{
    return "height: " + calculateGainLossDetailsContainerHeight() + "px";
}

// TRANSACTIONS
export const UPDATE_COST_BASIS_DIALOG_TITLE = "Update Cost Basis"
export const UPDATE_COST_BASIS_ERROR_TITLE = "Error updating the cost basis"
export const UPDATE_COST_BASIS_ERROR_MSG = "An error occurred while updating the cost basis, please try again. If this problem persists, please contact support."
export const UPDATE_TAX_TAGGING_ERROR_TITLE = "Error updating tax tagging"
export const UPDATE_TAX_TAGGING_ERROR_MSG = "Error updating tax tagging, please try again. If this problem persists, please contact support."
export const COST_BASIS_AMOUNT_MAX_DECIMALS = 18;

export const EDIT_TAX_TAGGING_TITLE = "Tax Tagging"
export const EDIT_TAX_TAGGING_TX_DETAILS = "Transaction details"
export const EDIT_TAX_TAGGING_TAG_TX = "Transaction Tag"
export const EDIT_TAX_TAGGING_DISTRIBUTION_TYPE_HEADER = "Distribution Type"
export const EDIT_TAX_TAGGING_LATE_REASON_HEADER = "Late Reason"
export const LATE_REASON_INSTR = "Please enter the reason for the postponed contribution."
export const LATE_REASON_MILITARY = "For participants' service in a combat zone, hazardous duty area, or direct support area, enter the appropriate executive order or public law, as defined under Special reporting for U.S. Armed Forces in designated combat zones, earlier."
export const LATE_REASON_AFFECTED_TAXPAYERS = "For participants who are “affected taxpayers,” as described in an IRS News Release relating to a federally designated disaster area, enter 'FD.'"
export const LATE_REASON_ROLLOVER_PLAN_LOAN = "For participants who are making a rollover of a qualified plan loan offset amount, enter 'PO.'"
export const LATE_REASON_ROLLOVER_CONTRIBUTION = "For participants who have certified that the rollover contribution is late because of one or more of the circumstances acceptable to the IRS, enter 'SC.'"

export const DEPOSIT_TRANSACTION_TYPES = ["DEPOSIT", "EXCHANGE_DEPOSIT", "FIAT_DEPOSIT", "PRO_DEPOSIT"]
export const WITHDRAWAL_TRANSACTION_TYPES = ["WITHDRAWAL", "EXCHANGE_WITHDRAWAL", "FIAT_WITHDRAWAL", "PRO_WITHDRAWAL"]
export const TRANSFER_TRANSACTION_TYPES = ["SUBACCOUNT_TRANSFER"]

// TAX TAGGING EVENTS
export const TAX_TAGGING_EVENT_CONTRIBUTION       = { title: "Contribution"         , value: TAX_TAG_CONTRIBUTION           , description: "New money added to this IRA for a specific tax year."}
export const TAX_TAGGING_EVENT_TRANSFER_IN        = { title: "Transfer-in"          , value: TAX_TAG_TRANSFER_IN            , description: "Non-reportable move of assets into this IRA from another retirement account."}
export const TAX_TAGGING_EVENT_CONVERSION         = { title: "Conversion"           , value: TAX_TAG_CONVERSION             , description: "Moving assets from a pre-tax account into a Roth IRA (taxable in the year of conversion)."}
export const TAX_TAGGING_EVENT_ROLLOVER           = { title: "Rollover"             , value: TAX_TAG_ROLLOVER               , description: "On-time rollover of assets from another retirement account into this IRA."}
export const TAX_TAGGING_EVENT_LATE_ROLLOVER      = { title: "Late-rollover"        , value: TAX_TAG_LATE_ROLLOVER          , description: "Rollover deposited after the normal 60-day window under late-rollover relief."}
export const TAX_TAGGING_EVENT_POSTPONED          = { title: "Postponed"            , value: TAX_TAG_POSTPONED              , description: "Prior-year contribution made after the usual filing deadline under permitted postponement rules."}
export const TAX_TAGGING_EVENT_RECHARACTERIZATION = { title: "Recharacterization"   , value: TAX_TAG_RECHARACTERIZATION     , description: "Treating a prior contribution as if it were made to a different type of IRA by moving it."}
export const TAX_TAGGING_EVENT_DISTRIBUTION       = { title: "Distribution"         , value: TAX_TAG_DISTRIBUTION           , description: "Money or assets paid out of this IRA to the account owner or beneficiary."}
export const TAX_TAGGING_EVENT_TRANSFER_OUT       = { title: "Transfer-out"         , value: TAX_TAG_TRANSFER_OUT           , description: "Non-reportable move of assets from this IRA to another IRA."}
export const TAX_TAGGING_EVENT_CONVERSION_OUT     = { title: "Conversion (Out)"     , value: TAX_TAG_CONVERSION_OUT         , description: "Moving assets out of this IRA as part of a conversion to another retirement account type."}

export const TAX_TAGGING_DEPOSIT_EVENTS_ROTH_IRA = [
    TAX_TAGGING_EVENT_CONTRIBUTION,
    TAX_TAGGING_EVENT_TRANSFER_IN,
    TAX_TAGGING_EVENT_CONVERSION,
    TAX_TAGGING_EVENT_ROLLOVER,
    TAX_TAGGING_EVENT_LATE_ROLLOVER,
    TAX_TAGGING_EVENT_POSTPONED
]

export const TAX_TAGGING_DEPOSIT_EVENTS_IRA = [
    TAX_TAGGING_EVENT_CONTRIBUTION,
    TAX_TAGGING_EVENT_TRANSFER_IN,
    TAX_TAGGING_EVENT_ROLLOVER,
    TAX_TAGGING_EVENT_LATE_ROLLOVER,
    TAX_TAGGING_EVENT_POSTPONED
]

export const TAX_TAGGING_WITHDRAWAL_EVENTS = [
    TAX_TAGGING_EVENT_DISTRIBUTION,
    TAX_TAGGING_EVENT_TRANSFER_OUT
]

export const TAX_TAGGING_TRANSFER_IN_EVENTS_ROTH_IRA = [
    TAX_TAGGING_EVENT_CONTRIBUTION,
    TAX_TAGGING_EVENT_TRANSFER_IN,
    TAX_TAGGING_EVENT_CONVERSION,
    TAX_TAGGING_EVENT_ROLLOVER,
    TAX_TAGGING_EVENT_LATE_ROLLOVER,
    TAX_TAGGING_EVENT_POSTPONED,
    TAX_TAGGING_EVENT_RECHARACTERIZATION,
]

export const TAX_TAGGING_TRANSFER_OUT_EVENTS_ROTH_IRA = [
    TAX_TAGGING_EVENT_DISTRIBUTION,
    TAX_TAGGING_EVENT_TRANSFER_OUT,
]

export const TAX_TAGGING_TRANSFER_IN_EVENTS_IRA = [
    TAX_TAGGING_EVENT_CONTRIBUTION,
    TAX_TAGGING_EVENT_TRANSFER_IN,
    TAX_TAGGING_EVENT_ROLLOVER,
    TAX_TAGGING_EVENT_LATE_ROLLOVER,
    TAX_TAGGING_EVENT_POSTPONED,
    TAX_TAGGING_EVENT_RECHARACTERIZATION,
]

export const TAX_TAGGING_TRANSFER_OUT_EVENTS_IRA = [
    TAX_TAGGING_EVENT_DISTRIBUTION,
    TAX_TAGGING_EVENT_CONVERSION_OUT,
    TAX_TAGGING_EVENT_TRANSFER_OUT,
]

export const DISTRIBUTION_TYPE_NORMAL_ID = "7"
export const DISTRIBUTION_TYPE_DIRECT_ID = "G"
export const DISTRIBUTION_TYPE_EARLY_ID = "1"
export const DISTRIBUTION_TYPE_EARLY_EXEP_ID = "2"
export const DISTRIBUTION_TYPE_QUALIFIED_ID = "Q"
export const DISTRIBUTION_TYPE_DEATH_ID = "4"
export const DISTRIBUTION_TYPE_DISABILITY_ID = "3"
export const DISTRIBUTION_TYPE_DESIGNATED_ID = "B"
export const DISTRIBUTION_TYPE_EARLY_ROTH_ID = "J"
export const DISTRIBUTION_TYPE_EARLY_ROTH_EXEP_ID = "T"
export const DISTRIBUTION_TYPE_EXCESS_CONTRIB_CURRENT_ID = "8"
export const DISTRIBUTION_TYPE_EXCESS_CONTRIB_PRIOR_ID = "P"
export const DISTRIBUTION_TYPE_RECHARACTERIZATION_CURRENT_ID = "N"
export const DISTRIBUTION_TYPE_RECHARACTERIZATION_PRIOR_ID = "R"

// TAX TAGGING - DISTRIBUTION TYPES
export const DISTRIBUTION_TYPE_NORMAL                       = { text: "Normal distribution"                                , value: DISTRIBUTION_TYPE_NORMAL_ID, enabled: true}
export const DISTRIBUTION_TYPE_DIRECT                       = { text: "Direct rollover / direct payment"                   , value: DISTRIBUTION_TYPE_DIRECT_ID, enabled: true}
export const DISTRIBUTION_TYPE_EARLY                        = { text: "Early distribution, no known exception"             , value: DISTRIBUTION_TYPE_EARLY_ID, enabled: true}
export const DISTRIBUTION_TYPE_EARLY_EXEP                   = { text: "Early distribution, exception applies"              , value: DISTRIBUTION_TYPE_EARLY_EXEP_ID, enabled: true}
export const DISTRIBUTION_TYPE_QUALIFIED                    = { text: "Qualified Roth IRA distribution"                    , value: DISTRIBUTION_TYPE_QUALIFIED_ID, enabled: true}
export const DISTRIBUTION_TYPE_DEATH                        = { text: "Death"                                              , value: DISTRIBUTION_TYPE_DEATH_ID, enabled: true}
export const DISTRIBUTION_TYPE_DISABILITY                   = { text: "Disability"                                         , value: DISTRIBUTION_TYPE_DISABILITY_ID, enabled: true}
export const DISTRIBUTION_TYPE_DESIGNATED                   = { text: "Designated Roth account distribution"               , value: DISTRIBUTION_TYPE_DESIGNATED_ID, enabled: true}
export const DISTRIBUTION_TYPE_EARLY_ROTH                   = { text: "Early Roth IRA distribution, no exception"          , value: DISTRIBUTION_TYPE_EARLY_ROTH_ID, enabled: true}
export const DISTRIBUTION_TYPE_EARLY_ROTH_EXEP              = { text: "Roth IRA distribution, exception applies"           , value: DISTRIBUTION_TYPE_EARLY_ROTH_EXEP_ID, enabled: true}
export const DISTRIBUTION_TYPE_EXCESS_CONTRIB_CURRENT       = { text: "Excess contribution plus earnings (current year)"   , value: DISTRIBUTION_TYPE_EXCESS_CONTRIB_CURRENT_ID, enabled: true}
export const DISTRIBUTION_TYPE_EXCESS_CONTRIB_PRIOR         = { text: "Excess contribution taxable in prior year"          , value: DISTRIBUTION_TYPE_EXCESS_CONTRIB_PRIOR_ID, enabled: true}
export const DISTRIBUTION_TYPE_RECHARACTERIZATION_CURRENT   = { text: "Recharacterization of contribution (current year)"  , value: DISTRIBUTION_TYPE_RECHARACTERIZATION_CURRENT_ID, enabled: true}
export const DISTRIBUTION_TYPE_RECHARACTERIZATION_PRIOR     = { text: "Recharacterization of contribution (prior year)"    , value: DISTRIBUTION_TYPE_RECHARACTERIZATION_PRIOR_ID, enabled: true}


export const DISTRIBUTION_TYPE_OPTIONS = [
    DISTRIBUTION_TYPE_NORMAL,
    DISTRIBUTION_TYPE_DIRECT,
    DISTRIBUTION_TYPE_EARLY,
    DISTRIBUTION_TYPE_EARLY_EXEP,
    DISTRIBUTION_TYPE_QUALIFIED,
    DISTRIBUTION_TYPE_DEATH,
    DISTRIBUTION_TYPE_DISABILITY,
    DISTRIBUTION_TYPE_DESIGNATED,
    DISTRIBUTION_TYPE_EARLY_ROTH,
    DISTRIBUTION_TYPE_EARLY_ROTH_EXEP,
    DISTRIBUTION_TYPE_EXCESS_CONTRIB_CURRENT,
    DISTRIBUTION_TYPE_EXCESS_CONTRIB_PRIOR,
    DISTRIBUTION_TYPE_RECHARACTERIZATION_CURRENT,
    DISTRIBUTION_TYPE_RECHARACTERIZATION_PRIOR
]


// ORDERS
export const EDIT_ORDER_SOLICITED_DIALOG_TITLE = "Update Solicited Status"
export const UPDATE_SOLICITED_STATUS_ERROR_TITLE = "Error updating solicited status"
export const UPDATE_SOLICITED_STATUS_ERROR_MSG = "An error occurred while updating the solicited status, please try again. If this problem persists, please contact support."

// UNREALIZED GAIN LOSS
export const SUMMARY_TAB = 1;
export const DETAILS_TAB = 2;

export const SUB_TAB_CONFIG = new Map([
    [ SUMMARY_TAB     , { "title": "Summary", "id": "summary" }]
,   [ DETAILS_TAB     , { "title": "Details", "id": "details" }]
]);


export const CALCULATION_METHOD   = "Calculation method: "
export const UNREALIZED_GAIN_LOSS = "Unrealized Gain/Loss"
export const YEAR                 = "Year"
export const NO_ACCOUNT_SELECTED  = "No account selected."
export const GENERATE_REPORT      = "Generate"
export const EXPORT_TABLE         = "Export Table"
export const ERROR_MESSAGE_NOTICE = "Failed to retrieve Unrealized Gain/Loss data."
export const CALC_START_MESSAGE   = "The data below is calculated using the "
export const CALC_END_MESSAGE     = " method. Click Generate to refresh."
export const NO_UNREALISED_DATA   = "Unrealized gain/loss data are only available if there is at least one buy transaction.";

// CSV EXPORT CONSTANTS
export const UNRELIAZED_GAIN_LOSS_REPORT = "unrealized_gain_loss_report_"
export const CSV_EXTENSION               = ".csv"
export const CSV_MIME_TYPE               = "data:text/csv;charset=utf-8,"
export const UNRELIAZED_CSV_HEADER       = "Account ID,Symbol,Transaction ID,Date,Quantity,Current Price, Market Value, Unit Price, Total Cost, Short-term G/L, Long-term G/L";
export const SHORT_TERM_GAIN_LOSS        = "Short Term"
export const LONG_TERM_GAIN_LOSS         = "Long Term"